import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Shared SEO component (Phase SEO-Global).
 * Now supports:
 *  - jsonLd as a single object OR an array (renders multiple <script> tags)
 *  - preloadFonts flag
 *  - hreflang: array of {lang, path}
 *  - authorName / articleMeta (for BlogPosting extras)
 */
const SITE_URL = 'https://winhousetutors.academy';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png?v=2`;

const Seo = ({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  jsonLd = null,
  noindex = false,
  hreflang = null,
  articleMeta = null,
}) => {
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const schemas = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={articleMeta ? 'article' : 'website'} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Winhouse Tutors" />

      {/* Article-specific OG (Facebook / LinkedIn) */}
      {articleMeta?.publishedAt && (
        <meta property="article:published_time" content={articleMeta.publishedAt} />
      )}
      {articleMeta?.updatedAt && (
        <meta property="article:modified_time" content={articleMeta.updatedAt} />
      )}
      {articleMeta?.author && (
        <meta property="article:author" content={articleMeta.author} />
      )}
      {articleMeta?.section && (
        <meta property="article:section" content={articleMeta.section} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* hreflang (ready for future translations — omit until content exists) */}
      {hreflang && hreflang.map((h) => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={`${SITE_URL}${h.path}`} />
      ))}

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
